import { compact } from 'lodash';
import type { Transaction } from 'src/apis/capacity-management/types';
import type {
  DealSurrenderability,
  DealsSurrenderabilityData,
} from 'src/apis/surrender-service/types';
import { getCreateSurrenderInquiryStepPaths as getBundledSurrenderInquiryStepPaths } from 'src/pages/reporting/surrender/create/steps-bundled/utils';
import { getCreateCamaSurrenderRequestStepPaths } from 'src/pages/reporting/surrender/create/steps-cama/utils';
import { getCreateSurrenderInquiryStepPaths as getUnbundledSurrenderInquiryStepPaths } from 'src/pages/reporting/surrender/create/steps-unbundled/utils';

type SurrenderAllocationIds = {
  key: keyof DealsSurrenderabilityData;
  ids: string[];
};

/**
 * The ids the surrender service knows the transaction by and the data field they belong to,
 * `undefined` for an invalidated transaction or a type that cannot be surrendered.
 * Entry side first, as the CaMa wizard takes the first id (like the legacy CaMa flow).
 */
const getSurrenderAllocationIds = (
  transaction: Transaction
): SurrenderAllocationIds | undefined => {
  if (transaction.status === 'INVALIDATED') return undefined;

  switch (transaction.transactionType) {
    case 'MULTI_YEAR_TRANSPORT_ALLOCATION':
      return {
        key: 'multiYearAllocationIds',
        ids: compact([
          transaction.entry?.multiYearTransportAllocationId,
          transaction.exit?.multiYearTransportAllocationId,
        ]),
      };

    case 'AUCTION':
      return {
        key: 'auctionAllocationIds',
        ids: compact([
          transaction.entry?.auctionAllocationId,
          transaction.exit?.auctionAllocationId,
        ]),
      };

    case 'TRANSFER_OF_USE_SELL':
    case 'TRANSFER_OF_USE_BUY':
    case 'ASSIGNMENT_SELL':
    case 'ASSIGNMENT_BUY':
      return {
        key: 'secondaryTradeIds',
        ids: compact([transaction.tradeId]),
      };

    case 'FCFS':
      return {
        key: 'fcfsAllocationIds',
        ids: compact([transaction.transportFcfsAllocationId]),
      };

    // the surrender service keys direct FCFS deals by request id, not by the side's allocation id
    case 'DIRECT_FCFS':
      return {
        key: 'fcfsAllocationIds',
        ids: compact([transaction.directFcfsRequestId]),
      };

    case 'SURRENDER':
    case 'CONVERSION':
    case 'RESHUFFLE':
    case 'RESHUFFLE_FROM':
      return undefined;
  }
};

/**
 * The `POST /deals/surrenderability` data, asking for both sides of bundles; `undefined` when
 * there is nothing to ask for.
 */
export const getSurrenderabilityData = (
  transactions: Transaction[]
): DealsSurrenderabilityData | undefined => {
  const surrenderAllocationIds = transactions.flatMap(
    (transaction) => getSurrenderAllocationIds(transaction) ?? []
  );

  const idsFor = (key: keyof DealsSurrenderabilityData) => [
    ...new Set(
      surrenderAllocationIds
        .filter((item) => item.key === key)
        .flatMap((item) => item.ids)
    ),
  ];

  const data: DealsSurrenderabilityData = {
    auctionAllocationIds: idsFor('auctionAllocationIds'),
    multiYearAllocationIds: idsFor('multiYearAllocationIds'),
    secondaryTradeIds: idsFor('secondaryTradeIds'),
    fcfsAllocationIds: idsFor('fcfsAllocationIds'),
  };

  return Object.values(data).some((ids) => ids.length > 0) ? data : undefined;
};

export type SurrenderFlow = 'cama' | 'standard';

type SurrenderableAllocation = {
  allocationId: string;
  surrenderFlow: SurrenderFlow;
  /** Human-readable deal id, prefilled in the standard (non-CaMa) wizard. */
  dealId?: string;
};

const getSurrenderFlow = (
  links: DealSurrenderability['_links']
): SurrenderFlow | undefined => {
  // The backend sends one of the links per allocation. With both, it is unclear which wizard to use.
  if (links?.createSurrenderRequest && links.createSurrenderRequestInquiry)
    return undefined;
  if (links?.createSurrenderRequest) return 'cama';
  if (links?.createSurrenderRequestInquiry) return 'standard';
  return undefined;
};

/** The allocation with the wizard the backend offers for it, `undefined` when not surrenderable. */
const getSurrenderableAllocation = (
  dealSurrenderabilities: DealSurrenderability[],
  allocationId: string
): SurrenderableAllocation | undefined => {
  const item = dealSurrenderabilities.find(
    (surrenderability) => surrenderability.allocationId === allocationId
  );
  const surrenderFlow = getSurrenderFlow(item?._links);

  return surrenderFlow
    ? { allocationId, surrenderFlow, dealId: item?.dealId }
    : undefined;
};

/**
 * The path to the wizard the backend offers for the transaction, `null` when there is none. Both
 * sides of a bundle need to offer the same wizard, so a bundle with a CaMa and a non-CaMa side gets
 * none.
 */
export const getCreateSurrenderPath = (
  transaction: Transaction,
  dealSurrenderabilities: DealSurrenderability[]
): string | null => {
  const allocationIds = getSurrenderAllocationIds(transaction)?.ids ?? [];
  const surrenderableAllocations = compact(
    allocationIds.map((allocationId) =>
      getSurrenderableAllocation(dealSurrenderabilities, allocationId)
    )
  );
  const [firstSurrenderableAllocation] = surrenderableAllocations;
  if (
    !firstSurrenderableAllocation ||
    surrenderableAllocations.length !== allocationIds.length ||
    surrenderableAllocations.some(
      ({ surrenderFlow }) =>
        surrenderFlow !== firstSurrenderableAllocation.surrenderFlow
    )
  ) {
    return null;
  }

  if (firstSurrenderableAllocation.surrenderFlow === 'cama') {
    return getCreateCamaSurrenderRequestStepPaths(
      firstSurrenderableAllocation.allocationId
    ).specify;
  }

  const { networkPointId } = transaction;
  if (networkPointId == null) return null;

  const { specify } =
    transaction.direction === 'BUNDLE'
      ? getBundledSurrenderInquiryStepPaths(networkPointId)
      : getUnbundledSurrenderInquiryStepPaths(networkPointId);
  const dealId = surrenderableAllocations.find(
    (allocation) => allocation.dealId
  )?.dealId;

  return dealId ? `${specify}?${new URLSearchParams({ dealId })}` : specify;
};
